import { generatePath } from 'react-router-dom';
import Cookies from 'js-cookie';

import { fill, find, includes, isArray, keys, map, omit } from 'lodash-es';
import { supportedLocales, SupportedLocales } from '@i18n/supported-locales';

import {
    AUDIOKINETIC_DOT_COM,
    COOKIE_USER_LOCALE,
    LOCAL_SERVER_REPLACE_LOOKUP,
    SUPPORTED_OS,
} from '@/constants';
import { isDev, isProd, isJakhub, isJakhubForChina } from './build-utils';

import { TIME_UNITS } from '@/constants';
const { MINUTE, HOUR, DAY, WEEK, MONTH, YEAR } = TIME_UNITS;

export { isJakhub, isDev, isProd, isJakhubForChina };

export const voidFunction = () => {};

export const cookieLanguage = () => {
    return Cookies.get(COOKIE_USER_LOCALE);
};

type PartialWindowLocation = {
    protocol: string;
    host: string;
};
export function backendHostNameForBlog(
    hostLocation: PartialWindowLocation,
    includeProtocol = true
) {
    const { protocol, host } = hostLocation;

    const protocolPrefix = includeProtocol ? `${protocol}//` : '';

    if (host.indexOf('audiokinetic.com') !== -1) {
        return host.indexOf('blog') !== -1
            ? `${protocolPrefix}${host.replace(/blog/g, 'www')}`
            : '';
    }

    if (host.indexOf('gowwise.com') !== -1) {
        return host.indexOf('blog-') !== -1 ? `${protocolPrefix}${host.replace(/blog-/g, '')}` : '';
    }

    return '';
}

export const extractDiff = (objLeft: Record<string, any>, objRight: Record<string, any>) => {
    const diffs = {
        leftIsMissing: {},
        rightIsMissing: {},
        valuesAreDifferent: {},
    } as Record<string, Record<string, string | Record<string, string>>>;

    const keysLeft = keys(objLeft);
    const keysRight = keys(objRight);

    keysLeft.forEach((k) => {
        if (!includes(keysRight, k)) {
            diffs.rightIsMissing[k] = objLeft[k];
        }

        if (includes(keysRight, k) && objLeft[k] !== objRight[k]) {
            diffs.valuesAreDifferent[k] = { l: objLeft[k], r: objRight[k] };
        }
    });

    keysRight.forEach((k) => {
        if (!includes(keysLeft, k)) {
            diffs.leftIsMissing[k] = objRight[k];
        }

        if (includes(keysLeft, k) && objLeft[k] !== objRight[k]) {
            diffs.valuesAreDifferent[k] = { l: objLeft[k], r: objRight[k] };
        }
    });

    return diffs;
};

export const stripHttp = (url: string): string =>
    url.toLowerCase().replace(new RegExp(/^(https?:|)\/\//g), '');

const knownProtocols = ['http', 'mail'];
export const isHttpLink = (path: string): boolean => knownProtocols.includes(path?.substring(0, 4));

export const extractDomainFromUrl = (url: string) => {
    const a = document.createElement('a');
    a.setAttribute('href', url);
    return a.hostname;
};
export const isStaticImport = (path: string): boolean =>
    path.indexOf('/jakhub/static/media/') !== -1 || path.indexOf('/assets/') !== -1;

export const isNonHttpProtocol = (path: string): boolean =>
    path?.indexOf('://') !== -1 && !isHttpLink(path);

export const insertParams = (path: string, params: any): string => {
    const splitter = '://';
    if (path?.indexOf(splitter) === -1) return generatePath(path, params);

    const explicitUrl = path.split(splitter);
    return [explicitUrl[0], generatePath(explicitUrl[1], params)].join(splitter);
};

export const isNonAkLink = (path = ''): boolean => {
    // To be a non AK link it has to be fully qualified
    const isAnExplicitLink = isHttpLink(path);

    // If it's a media.gowwise link we need a new window
    const isAnExternalMediaLink = path.indexOf('media.gowwise.') !== -1;

    // audiokinetic AND gowwise should be considered internal NO-NEW-TAB links
    const isAnAkLink = path.indexOf('.audiokinetic.') === -1 && path.indexOf('.gowwise') === -1;

    return isAnExplicitLink && (isAnExternalMediaLink || isAnAkLink);
};

export const isExternalLink = (path = ''): boolean => {
    if (path.slice(-3).toLowerCase() === 'pdf') return true;

    if (!isHttpLink(path)) return false;

    if (path.indexOf('media.gowwise') !== -1) return true;
    if (path.indexOf('://info.audiokinetic') !== -1) return true;

    return !(path.indexOf('.audiokinetic.') !== -1 || path.indexOf('gowwise') !== -1);
};

export const injectLanguageSlug = (
    path: string,
    slug: string,
    includeLanguageSlug: boolean
): string => {
    const pathIsExplicit = path.indexOf('://') !== -1;

    if (!includeLanguageSlug) return path;

    if (isNonAkLink(path) || isExternalLink(path)) return path;
    if (!pathIsExplicit) return `/${slug}${path}`;

    const [protocol, fullPath] = path.split('://');
    const [domain, ...rest] = fullPath.split('/');
    return `${protocol}://${[domain, slug].concat(...rest).join('/')}`;
};

export const protocolHostnamePath = (contextWindow = window): string => {
    const host = contextWindow?.location?.host;
    const protocol = contextWindow?.location?.protocol;

    const triggerString = host?.indexOf('audiokinetic.com') !== -1 ? 'blog' : 'blog-';
    const targetString = host?.indexOf('audiokinetic.com') !== -1 ? 'www' : '';

    const targetHostName = host?.replace(triggerString, targetString);

    return `${protocol}//${targetHostName}`;
};

export const getHardPathTarget = (
    contextWindow: Window | any = typeof window !== 'undefined' ? window : global
): string => {
    return isDev() || isJakhubForChina()
        ? AUDIOKINETIC_DOT_COM
        : protocolHostnamePath(contextWindow);
};

export const getFallbackBackendServer = (
    contextWindow: Window | any = typeof window !== 'undefined' ? window : global
): string => {
    const {
        location: { host, protocol },
    } = contextWindow;

    return !isDev()
        ? `${protocol}//${host}`
        : LOCAL_SERVER_REPLACE_LOOKUP[host] || `${protocol}//${host}`;
};

export function getCurrentPlatform(): 'linux' | 'windows' | 'mac' {
    if (navigator.userAgent.includes('Win')) {
        return 'windows';
    }
    if (navigator.userAgent.includes('Mac')) {
        return 'mac';
    }
    if (navigator.userAgent.includes('Linux')) {
        return 'linux';
    }
    // fallback for unknown OS
    return 'windows';
}

export const rotateArray = (array: any[], rotateBy: number) => {
    const n = array?.length;
    const cleanRotateBy = rotateBy % n;

    return array.slice(cleanRotateBy).concat(array.slice(0, cleanRotateBy));
};

export const balanceArrayLines = (
    inputArray: Array<unknown>,
    maxItemsPerLine = 9
): Array<number> => {
    /*
    1-9 -> 1-9
    10 -> 5 + 5
    11 -> 6 + 5
    12 -> 6 + 6
    13 -> 7 + 6
    14 -> 7 + 7
    15 -> 8 + 7
    16 -> 8 + 8
    17 -> 9 + 8
    18 -> 9 + 9
    19 -> 7 + 6 + 6
    20 -> 7 + 7 + 6
    21 -> 7 + 7 + 7
    22 -> 7 + 7 + 7
    23 -> 8 + 7 + 7
    24 -> 8 + 8 + 8
    25 -> 9 + 8 + 8
    26 -> 9 + 9 + 8
    27 -> 9 + 9 + 9
    28 -> 7 + 7 + 7 + 7
    29 -> 8 + 7 + 7 + 7
    30 -> 8 + 8 + 7 + 7
    31 -> 8 + 8 + 8 + 7
    32 -> 8 + 8 + 8 + 8
    33 -> 9 + 8 + 8 + 8
    34 -> 9 + 9 + 8 + 8
    35 -> 9 + 9 + 9 + 8
    36 -> 9 + 9 + 9 + 9
    37 -> 8 + 8 + 7 + 7 + 7
    38 -> 8 + 8 + 8 + 7 + 7
    39 -> 8 + 8 + 8 + 8 + 7
    40 -> 8 + 8 + 8 + 8 + 8
    41 -> 9 + 8 + 8 + 8 + 8
    42 -> 9 + 9 + 8 + 8 + 8
    43 -> 9 + 9 + 9 + 8 + 8
    44 -> 9 + 9 + 9 + 9 + 8
    45 -> 9 + 9 + 9 + 9 + 9
    46 -> 8 + 8 + 8 + 8 + 7 + 7
    47 -> 8 + 8 + 8 + 8 + 8 + 7
    48 -> 8 + 8 + 8 + 8 + 8 + 8
    49 -> 9 + 8 + 8 + 8 + 8 + 8
    50 -> 9 + 9 + 8 + 8 + 8 + 8
 */
    const numberOfLines = Math.ceil(inputArray?.length / maxItemsPerLine);
    const minItemsPerLine = Math.floor(inputArray?.length / numberOfLines);
    const arrayConfig = fill(Array(numberOfLines), minItemsPerLine);
    let distribute = inputArray?.length - numberOfLines * minItemsPerLine;
    let listIndex = 0;
    while (distribute) {
        arrayConfig[listIndex++]++;
        distribute--;
    }
    return arrayConfig;
};

export const capitalize = (input = ''): string => input.charAt(0).toUpperCase() + input.slice(1);
export const uncapitalize = (input = ''): string => input.charAt(0).toLowerCase() + input.slice(1);

export function snakeToPascal(input: string): string {
    if (!input.includes('_') && /^[A-Z][A-Za-z0-9]*$/.test(input)) {
        return input;
    }

    return input
        .toLowerCase()
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join('');
}

export const toCamelCase = (input = ''): string => {
    let rep = input;
    rep = rep.replace(new RegExp(/&/g), '');
    rep = rep
        .split(' ')
        .map((word) => capitalize(word))
        .join('');
    rep = uncapitalize(rep);
    return rep;
};

export const localeLookup: Record<SupportedLocales, string> = {
    [SupportedLocales.EN_US]: 'en_US',
    [SupportedLocales.FR_CA]: 'fr_FR',
    [SupportedLocales.JA_JP]: 'ja_JP',
    [SupportedLocales.KO_KR]: 'ko_KR',
    [SupportedLocales.ZH_CN]: 'zh_CN',
};

export const languageToLocale = (language: string | undefined) =>
    find(SupportedLocales, (supLang) => supLang.substring(0, 2) === language) ||
    supportedLocales[0];

export const localeToLanguage = (locale: string): string =>
    locale.split(new RegExp('[-_]', 'g'))[0];

export const removeDuplicates = (arr: string[]) => [...new Set(arr)];

export const sanitizeAndCapitalizeLanguages = (languages: string[]) => {
    return removeDuplicates(languages?.map((l: string) => capitalize(l.toLowerCase())));
};

export const snakeCase = (input = ''): string => input.replace(/-/g, '_');
export const upperSnakeCase = (input = ''): string => snakeCase(input).toUpperCase();
export const lowerSnakeCase = (input = ''): string =>
    input
        .replace(/[A-Z]/g, (letter, index) => `${index !== 0 ? '_' : ''}${letter.toLowerCase()}`)
        .replace(/- /g, '_')
        .toLowerCase();
export const stripNonAlphaNumeric = (input = ''): string => input.replace(/[\W_]+/g, '');

export const noSpaceDash = (str: string): string => str.replace(new RegExp(/[\s-]/g), '');

export const accentsTidy = (str: string): string => {
    let rep = str;
    rep = rep.replace(new RegExp(/[àáâãäå]/g), 'a');
    rep = rep.replace(new RegExp(/[ÀÁÂÃÄÅ]/g), 'A');
    rep = rep.replace(new RegExp(/[èéêë]/g), 'e');
    rep = rep.replace(new RegExp(/[ÈÉÊË]/g), 'E');
    rep = rep.replace(new RegExp(/[ìíîï]/g), 'i');
    rep = rep.replace(new RegExp(/[ÌÍÎÏ]/g), 'I');
    rep = rep.replace(new RegExp(/[òóôõö]/g), 'o');
    rep = rep.replace(new RegExp(/[ÒÓÔÕÖ]/g), 'O');
    rep = rep.replace(new RegExp(/[ùúûü]/g), 'u');
    rep = rep.replace(new RegExp(/[ÙÚÛÜ]/g), 'U');
    rep = rep.replace(new RegExp(/[ýÿ]/g), 'y');
    rep = rep.replace(new RegExp(/[ÝŸ]/g), 'Y');
    rep = rep.replace(new RegExp(/æ/g), 'ae');
    rep = rep.replace(new RegExp(/Æ/g), 'AE');
    rep = rep.replace(new RegExp(/ç/g), 'c');
    rep = rep.replace(new RegExp(/Ç/g), 'C');
    rep = rep.replace(new RegExp(/ñ/g), 'n');
    rep = rep.replace(new RegExp(/Ñ/g), 'N');
    rep = rep.replace(new RegExp(/œ/g), 'oe');
    rep = rep.replace(new RegExp(/Œ/g), 'OE');
    return rep;
};

export const extractEnvName = (hostname: string): string | null => {
    const hostnameSplit = hostname.split('.');
    if (hostnameSplit?.length !== 3) {
        return null;
    }
    const subDomainSplit = hostnameSplit[0].split('-');
    if (subDomainSplit?.length > 1) return subDomainSplit[1];
    return 'prod';
};

export const isValidEmail = (email: string): Boolean =>
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()\\[\].,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        String(email).toLowerCase()
    );

export const getRelativeTime = (date, lang = 'en') => {
    const formatter = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });

    const now = new Date();
    const testDate = new Date(date);
    const diffSeconds = (testDate.getTime() - now.getTime()) / 1000;

    if (Math.abs(diffSeconds) < 60) {
        return formatter.format(Math.round(diffSeconds), 'second');
    }
    if (Math.abs(diffSeconds) < 3600) {
        return formatter.format(Math.round(diffSeconds / 60), 'minute');
    }
    if (Math.abs(diffSeconds) < 86400) {
        return formatter.format(Math.round(diffSeconds / 3600), 'hour');
    }
    if (Math.abs(diffSeconds) < 604800) {
        // 7 days
        return formatter.format(Math.round(diffSeconds / 86400), 'day');
    }
    if (Math.abs(diffSeconds) < 2592000) {
        // approx 30 days
        return formatter.format(Math.round(diffSeconds / 604800), 'week');
    }
    if (Math.abs(diffSeconds) < 31536000) {
        // approx 365 days
        return formatter.format(Math.round(diffSeconds / 2592000), 'month');
    }
    return formatter.format(Math.round(diffSeconds / 31536000), 'year');
};

export const convertToSeconds = (duration: string): number => {
    if (!duration) return 0;
    const [H = '0', M = '0', S = '0'] = duration?.split(':');
    return parseInt(H, 10) * HOUR + parseInt(M, 10) * MINUTE + parseInt(S, 10);
};

export const getApproximateTime = (seconds: number) => {
    const lang = cookieLanguage();

    const formatter = new Intl.DurationFormat(lang, { style: 'narrow' });
    let cumulativeSeconds = Math.max(seconds, 60000);
    let memory = 0;
    const duration = {};

    if (cumulativeSeconds >= YEAR) {
        memory = Math.floor(cumulativeSeconds / YEAR);
        cumulativeSeconds -= memory * YEAR;
        duration.years = memory;
    }

    if (cumulativeSeconds >= MONTH) {
        memory = Math.floor(cumulativeSeconds / MONTH);
        cumulativeSeconds -= memory * MONTH;
        duration.months = memory;
    }

    if (cumulativeSeconds >= WEEK) {
        memory = Math.floor(cumulativeSeconds / WEEK);
        cumulativeSeconds -= memory * WEEK;
        duration.weeks = memory;
    }

    if (cumulativeSeconds >= DAY) {
        memory = Math.floor(cumulativeSeconds / DAY);
        cumulativeSeconds -= memory * DAY;
        duration.days = memory;
    }

    if (cumulativeSeconds >= HOUR) {
        memory = Math.floor(cumulativeSeconds / HOUR);
        cumulativeSeconds -= memory * HOUR;
        duration.hours = memory;
    }

    if (cumulativeSeconds >= MINUTE) {
        memory = Math.floor(cumulativeSeconds / MINUTE);
        cumulativeSeconds -= memory * MINUTE;
        duration.minutes = memory;
    }

    duration.cumulativeSeconds = Math.floor(cumulativeSeconds / 1000);

    return formatter.format(omit(duration, ['seconds']));
};

export const stringToDate = (dateString: string): Date => {
    const dateArray = dateString.split('-').map((piece) => parseInt(piece, 10));
    return new Date(dateArray[0], dateArray[1] - 1, dateArray[2]);
};

export function randomInteger(min: number, max: number) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

export const dateToString = (date: Date): string => {
    return [
        date.getUTCFullYear(),
        `${date.getUTCMonth() + 1}`.padStart(2, '0'),
        `${date.getUTCDate()}`.padStart(2, '0'),
    ].join('-');
};

export const timeToString = (time: Date): string => {
    return [
        time.getUTCHours(),
        `${time.getUTCMinutes()}`.padStart(2, '0'),
        `${time.getUTCSeconds()}`.padStart(2, '0'),
    ].join(':');
};

export const extractFromDateTime = (dateTime: any) => {
    const newDate = new Date(dateTime || 0);

    const date = newDate.toISOString()?.split('T')[0];
    const time = newDate.toTimeString().substring(0, 8);

    return { date, time };
};

export const absoluteDate = (dateString: string) => {
    const dateArray = dateString.split('-').map((piece) => parseInt(piece, 10));
    const date = new Date(dateArray[0], dateArray[1] - 1, dateArray[2]);
    const userTimezoneOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() + userTimezoneOffset);
};

export const localeDate = (dateString: string, userLocale: SupportedLocales | 'en-CA') =>
    Intl.DateTimeFormat(userLocale).format(new Date(dateString));

export const shuffleArray = (array: any[]): any[] => {
    const mappedArray = map(array, (i) => i);
    let m = mappedArray?.length,
        t,
        i;

    while (m) {
        i = Math.floor(Math.random() * m--);
        t = mappedArray[m];
        mappedArray[m] = mappedArray[i];
        mappedArray[i] = t;
    }

    return mappedArray;
};

export const setValueWithPath = (obj: any, path: string, value: any) => {
    const a = path.split('.');
    let o = obj;

    while (a.length - 1) {
        const n = a.shift() || '';
        if (!o?.[n]) o[n] = {};
        o = o[n];
    }

    o[a[0]] = value;
};

export const getValueWithPath = (obj: any, path: string) => {
    if (!obj || !path) {
        return null;
    }
    path = path.replace(/\[(\w+)\]/g, '.$1');
    path = path.replace(/^\./, '');
    const a = path.split('.');
    let o = obj;
    while (a.length) {
        const n = a.shift() || '';
        if (o?.[n] === undefined) return;
        o = o[n];
    }
    return o;
};

export const setsAreEqual = (setA: any, setB: any) =>
    setA.size === setB.size && [...setA].every((element) => setB.has(element));

export async function detectOs() {
    const platform = await navigator.userAgentData?.platform;

    if (platform?.indexOf('Win') !== -1) return SUPPORTED_OS.WINDOWS;
    if (platform?.indexOf('Mac') !== -1) return SUPPORTED_OS.MACOS;
    if (platform?.indexOf('Linux') !== -1) return SUPPORTED_OS.LINUX;
    return 'unsuppported_version';
}

const loremIpsumText =
    'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed non ultricies sapien. Praesent at lorem non felis blandit aliquet. Cras ultrices orci sed efficitur blandit. Curabitur in tincidunt nulla, sed varius libero. Etiam consectetur enim dignissim scelerisque condimentum. Morbi sem nulla, egestas et malesuada sit amet, ornare eu sapien. Mauris porttitor lacinia pellentesque. Aliquam rutrum lacinia ex, a rutrum dui aliquet vitae. Aenean egestas scelerisque ullamcorper. Aliquam mattis ultricies nisl, ut accumsan purus congue ut. Ut lectus tellus, egestas id nunc sodales, viverra varius mauris.\n In pulvinar, nisi nec suscipit dapibus, urna magna ultricies ex, eu pellentesque neque tellus quis dui. Sed ac arcu et velit egestas bibendum ac vel eros. Fusce pharetra dapibus leo ut molestie. Vestibulum scelerisque lorem turpis, at accumsan turpis accumsan ac. Nam eget venenatis augue. Pellentesque at cursus libero, vitae tempor ipsum. Sed pulvinar in mauris vel imperdiet. Donec in nunc sodales, congue orci eget, rhoncus enim. Fusce sed laoreet sem, sit amet fermentum erat. In malesuada egestas nibh, at placerat dolor aliquam varius. Ut rutrum a arcu a finibus. Suspendisse ut massa ut velit cursus efficitur vel nec libero. Fusce dictum lacinia ligula. Curabitur nec malesuada velit, vel egestas dui. Etiam scelerisque, lorem quis rutrum viverra, purus lectus ultrices orci, non dignissim nisi elit et arcu. Interdum et malesuada fames ac ante ipsum primis in faucibus.\n\nDuis tincidunt vulputate felis, in scelerisque nibh interdum consectetur. Vestibulum vel fermentum ante. Donec felis ante, placerat vitae auctor in, suscipit eu felis. Nam sollicitudin hendrerit eros, nec tempus arcu suscipit at. Cras at dignissim metus, in bibendum nunc. Phasellus a arcu ac purus porttitor ornare a condimentum leo. Donec dignissim feugiat ornare. Maecenas sem urna, pretium eget erat ut, mollis dignissim metus.\n\nMauris dapibus in nisl ac ultricies. Nunc sapien velit, aliquet ac interdum quis, lacinia eget nisi. Aliquam eu congue tellus. Duis eu magna vitae mi feugiat congue ut posuere orci. Nulla molestie nisi vitae purus ultricies posuere. Proin scelerisque at turpis vitae accumsan. Duis accumsan egestas est vel placerat. Aliquam tempus nisi ipsum, porta condimentum ipsum laoreet in.\n\nSuspendisse sed commodo justo. Suspendisse consequat lorem nulla, sit amet iaculis mi vehicula ac. Maecenas interdum ornare congue. Vivamus scelerisque vel erat quis ultrices. Morbi posuere ut purus et sodales. Cras quis convallis nibh, quis venenatis nibh. Integer pretium laoreet felis, non placerat quam pretium eget. Aenean euismod nibh eu urna commodo maximus. In tempus orci eu facilisis euismod. Maecenas non fringilla arcu, non dapibus ante.\n\nMaecenas auctor pulvinar felis, vel accumsan sapien ullamcorper non. Aenean venenatis elementum ex, ac commodo nisl dignissim nec. Vestibulum gravida lectus quis odio convallis fringilla. Nam felis elit, tristique ac tincidunt sit amet, mollis et quam.';
export const loremIpsum = (numberOfWords: number) => {
    const words = shuffleArray(loremIpsumText.split(' '));

    return numberOfWords <= words.length
        ? words.slice(0, numberOfWords).join(' ')
        : words.join(' ');
};

export const convertBytesTo = (nbBytes: number) => {
    let unitIndex = 0;
    let newSize = nbBytes;
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    while (newSize >= 1024) {
        newSize = Math.floor(newSize / 1024);
        unitIndex++;
    }

    return `${newSize}${units[unitIndex]}`;
};

export const nextArrayIndex = (idxToTest: number, arraySize: number, loop = false): number => {
    if (0 <= idxToTest && idxToTest < arraySize) return idxToTest;

    if (!loop) return Math.max(Math.min(idxToTest, arraySize - 1), 0);

    return 0 <= idxToTest && idxToTest < arraySize
        ? idxToTest
        : Math.abs(Math.abs(idxToTest) - arraySize);
};

export const serializeObjectForUrl = (obj: Record<string, string | number | boolean>) => {
    const outputString: string[] = [];
    Object.keys(obj).forEach((key) => {
        if (obj[key]) outputString.push(`${key}=${obj[key]}`);
    });
    return outputString.join('&');
};

export const valueSort = (objectToSort: Record<string, any>) => {
    const entries = Object.entries(objectToSort);
    entries.sort((a, b) => a[0].localeCompare(b[0]));
    return Object.fromEntries(entries);
};

export const intersection = <P>(a: P | P[], b: P | P[]): P[] => {
    // string, number, boolean
    if (typeof a !== 'object' && typeof b !== 'object') return a === b ? [a] : [];

    // array & string
    if (isArray(a) && typeof b !== 'object') return a.includes(b) ? [b] : [];

    // string & array
    if (typeof a !== 'object' && isArray(b)) return b.includes(a) ? [a] : [];

    // array & array
    if (isArray(a) && isArray(b)) {
        const intersectionArray: P[] = [];

        a?.forEach((item: P) => {
            if (b?.includes(item)) intersectionArray.push(item);
        });
        return intersectionArray;
    }
    return [];
};

export const hasIntersection = <P>(a: P | P[], b: P | P[]): boolean =>
    intersection(a, b).length > 0;

/**
 * Parse authors as a string list
 * @param authors
 * @returns List of form 'Author1, Author2 & Author3'
 */
export const parseAuthors = (authors: string[]) => {
    return authors?.join(', ').replace(/,([^,]*)$/, ' & $1');
};

export const getAssetUrl = (path: string): string => {
    if (!path) return '';

    if (/^(https?:)?\/\//i.test(path)) return path;

    const base = (import.meta.env.BASE_URL ?? '/').replace(/\/+$/, '');
    const cleanPath = path.replace(/^\/+/, '');

    let final = path.startsWith(base + '/') ? path : `${base}/${cleanPath}`;
    if (!final.startsWith('/')) final = `/${final}`;

    if (typeof window !== 'undefined' && window.location?.origin) {
        return new URL(final, window.location.origin).href;
    }

    return final;
};
